import { Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import { PlanType } from '@constants/plans';

import { useGetStripeCustomer, useStripePlans } from '@hooks/api/stripe';
import { useSession } from '@hooks/useSession';

import patientPlanImg from '@images/plan-images/undraw_Personal_file_re_5joy.png';
import caregiverPlanImg from '@images/plan-images/undraw_medical_care_movn.png';

import { formatMoney } from '@utils/format';

import PlanCard from './PlanCard';

interface PlanSelectionProps {
  /** Callback that fires when a plan is selected */
  onSelectPlan?: (planType: PlanType) => void;
  canAddPatients?: boolean;
}

const messages = defineMessages({
  title: {
    id: 'planSelection.title',
    defaultMessage: 'Select the type of user you want to add to your account',
  },
  patientTitle: {
    id: 'planSelection.plan.patient.title',
    defaultMessage: 'Patient',
  },
  noCost: {
    id: 'planSelection.cost.free',
    defaultMessage: 'Free',
  },
  monthlyCost: {
    id: 'planSelection.cost.month',
    defaultMessage: '{cost}/month',
  },
  yearlyCost: {
    id: 'planSelection.cost.year',
    defaultMessage: '{cost}/year',
  },
  caregiverTitle: {
    id: 'planSelection.plan.caregiver.title',
    defaultMessage: 'Caregiver',
  },
  patientDescription1: {
    id: 'planSelection.plan.patient.description.1',
    defaultMessage:
      'This user will have their own profile with their own records',
  },
  patientDescription2: {
    id: 'planSelection.plan.patient.description.2',
    defaultMessage:
      'You can give them access to none, some, or all of your records',
  },
  patientDescription3: {
    id: 'planSelection.plan.patient.description.3',
    defaultMessage:
      'If they are under the age of 18, you will have full access to their profile',
  },
  caregiverDescription1: {
    id: 'planSelection.plan.caregiver.description.1',
    defaultMessage:
      'This user is someone who you want to give access to some or all of your records',
  },
  caregiverDescription2: {
    id: 'planSelection.plan.caregiver.description.2',
    defaultMessage:
      'This user will not have their own records in their profile',
  },
  caregiverDescription3: {
    id: 'planSelection.plan.caregiver.description.3',
    defaultMessage:
      'Examples include: family members, friends, power of attorney',
  },
});

const PlanSelection = ({
  onSelectPlan,
  canAddPatients = true,
}: PlanSelectionProps) => {
  const {
    state: { activeProfile },
  } = useSession();
  const { formatMessage } = useIntl();
  const handleSelectPlan = (planType: PlanType) => {
    onSelectPlan?.(planType);
  };

  const { data: customerData, loading: customerLoading } = useGetStripeCustomer(
    { account_id: activeProfile?.account_id }
  );
  const { data: plansData, loading: plansLoading } = useStripePlans();

  const loading = customerLoading || plansLoading;

  const { PATIENT } = plansData;
  const currency = customerData?.getStripeCustomer.currency;

  const subcMonth = PATIENT?.getSubscriptionCost('month', currency ?? '');
  const subcYear = PATIENT?.getSubscriptionCost('year', currency ?? '');

  const formattedSubMonth = formatMessage(messages.monthlyCost, {
    cost: formatMoney(
      subcMonth,
      2,
      undefined,
      undefined,
      currency ?? undefined
    ),
  });
  const formattedSubYear = formatMessage(messages.yearlyCost, {
    cost: formatMoney(subcYear, 2, undefined, undefined, currency ?? undefined),
  });

  return (
    <>
      <Typography variant="subtitle1">
        {formatMessage(messages.title)}
      </Typography>
      <ContentLoader loading={loading}>
        <Stack spacing={1} direction="row">
          {canAddPatients ? (
            <PlanCard
              price={`${formattedSubMonth}, ${formattedSubYear}`}
              planType={PlanType.Patient}
              imageSrc={patientPlanImg}
              onClick={handleSelectPlan}
              title={formatMessage(messages.patientTitle)}
              description={[
                formatMessage(messages.patientDescription1),
                formatMessage(messages.patientDescription2),
                formatMessage(messages.patientDescription3),
              ]}
            />
          ) : null}
          <PlanCard
            price={formatMessage(messages.noCost)}
            planType={PlanType.Caregiver}
            imageSrc={caregiverPlanImg}
            onClick={handleSelectPlan}
            title={formatMessage(messages.caregiverTitle)}
            description={[
              formatMessage(messages.caregiverDescription1),
              formatMessage(messages.caregiverDescription2),
              formatMessage(messages.caregiverDescription3),
            ]}
          />
        </Stack>
      </ContentLoader>
    </>
  );
};

export default PlanSelection;
